import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from "motion/react";
import { TicketIcon, ChevronDownIcon } from '@/components/icons';
import { TicketCard } from './TicketCard';
import { getBestImageUrl, formatDate } from '@/lib/utils';
import { CartStatus, CartStatusLabels } from '@/features/cart/types';
import type { CartResponse } from '@/features/cart/types';
import type { BookingResponse } from '@/features/booking/types/bookingTypes';

interface CartGroupCardProps {
  cart: CartResponse;
  bookings: Array<{ booking: BookingResponse }>;
  onBookingClick: (bookingId: string) => void;
}

export const CartGroupCard: React.FC<CartGroupCardProps> = ({ 
  cart, 
  bookings, 
  onBookingClick 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const bookingCount = bookings.length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const business = cart.business;
  if (!business) return null;

  const backgroundImage = getBestImageUrl(business.card_image_id, business.media, '/placeholder-club.jpg');
  const formattedExpiration = formatDate(cart.expires_at);

  const getStatusBadgeClass = (status: CartStatus) => {
    switch (status) {
      case CartStatus.Active:
        return 'bg-green-500/90 text-white border-green-400';
      case CartStatus.PendingPayment:
        return 'bg-yellow-500/90 text-white border-yellow-400';
      case CartStatus.SuccessfulPayment:
      case CartStatus.Completed:
        return 'bg-blue-500/90 text-white border-blue-400';
      case CartStatus.FailedPayment:
        return 'bg-red-500/90 text-white border-red-400';
      case CartStatus.Abandoned:
        return 'bg-gray-600/90 text-white border-gray-400';
      case CartStatus.Refunded:
        return 'bg-orange-500/90 text-white border-orange-400';
      default:
        return 'bg-gray-600/90 text-white border-gray-400';
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't toggle if clicking on the dropdown button or dropdown menu
    const target = e.target as HTMLElement;
    if (target.closest('[data-dropdown-trigger]') || target.closest('[data-dropdown-menu]')) {
      return;
    }
    // Clicking anywhere on card expands/collapses bookings (like event card)
    setIsExpanded(!isExpanded);
  };

  return (
    <motion.div 
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -4 }}
      onClick={handleCardClick}
      className={`relative rounded-2xl overflow-hidden shadow-lg group cursor-pointer ${cart.is_guest_cart ? 'ring-1 ring-gray-500/40' : ''}`}
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: cart.is_guest_cart ? '1px solid rgba(156, 163, 175, 0.3)' : '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      {/* Background Image - Full Width */}
      <div className="relative h-48 overflow-hidden">
        <motion.img
          src={backgroundImage}
          alt={business.name}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.5 }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/placeholder-club.jpg';
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
        
        {/* Status Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="absolute top-4 left-4 z-10 flex items-center gap-2"
        >
          <span className={`px-4 py-1.5 rounded-full text-sm font-bold border-2 backdrop-blur-sm ${getStatusBadgeClass(cart.status)}`}>
            {CartStatusLabels[cart.status]}
          </span>
          {cart.is_guest_cart && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-700/80 backdrop-blur-sm border border-gray-500/60 shadow-lg z-30"
            >
              <svg 
                className="w-5 h-5 text-gray-300" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                />
              </svg>
              <span className="text-sm font-semibold text-gray-300">Guest Cart</span>
            </motion.div>
          )}
        </motion.div>

      </div>

      {/* Content - Glass Morphism Background */}
      <div 
        className="relative p-4 border-t border-white/20"
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.08))',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        {/* Business Name and Location */}
        <div className="mb-1">
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-xl font-bold text-white mb-1"
          >
            {business.name}
          </motion.h3>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center gap-2 text-gray-400 mb-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-xs">{business.location.city}, {business.location.country}</span>
          </motion.div>
        </div>

        {/* Expiration and Bookings Count */}
        <div className="flex items-center gap-4 text-sm text-gray-400">
          {/* Expiration Date */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Expires: {formattedExpiration}</span>
          </motion.div>

          {/* Bookings Count */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="flex items-center gap-2"
          >
            <TicketIcon className="w-4 h-4" />
            <span>{bookingCount} {bookingCount === 1 ? 'booking' : 'bookings'}</span>
          </motion.div>
        </div>
      </div>

      {/* Dropdown Button - Bottom Right */}
      <div className="absolute bottom-4 right-4 z-40">
        <motion.button
          data-dropdown-trigger
          onClick={(e) => {
            e.stopPropagation();
            setIsDropdownOpen(!isDropdownOpen);
          }}
          className="p-2 bg-gray-800/90 backdrop-blur-md text-white rounded-lg hover:bg-gray-700/90 transition-all duration-200 border border-gray-700/50 shadow-lg"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <motion.div
            animate={{ rotate: isDropdownOpen ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDownIcon className="w-5 h-5" />
          </motion.div>
        </motion.button>
      </div>

      {/* Dropdown Menu - Bottom Right */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            data-dropdown-menu
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-14 right-4 z-50"
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <div 
              className="bg-gray-800/95 backdrop-blur-md border border-gray-700 rounded-lg shadow-xl min-w-[160px] overflow-hidden"
              style={{
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              }}
            >
              <button
                onClick={() => {
                  setIsExpanded(!isExpanded);
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-3 text-left text-sm text-white hover:bg-gray-700/50 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isExpanded ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                </svg>
                {isExpanded ? 'Collapse' : 'Expand'} Bookings
              </button>
              <div className="border-t border-gray-700"></div>
              <button
                onClick={() => {
                  // Add more actions here if needed
                  setIsDropdownOpen(false);
                }}
                className="w-full px-4 py-3 text-left text-sm text-white hover:bg-gray-700/50 transition-colors"
              >
                View Details
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Expanded Bookings Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            className="border-t border-white/20 p-4"
            style={{
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.04))',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ 
              duration: 0.4, 
              ease: "easeInOut",
              opacity: { duration: 0.2 }
            }}
          >
            <motion.div 
              className="space-y-2 sm:space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-white mb-2 sm:mb-4">
                Bookings ({bookingCount})
              </h4>
              <motion.div 
                className="space-y-2 sm:space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                {bookings
                  .filter((item) => item?.booking != null)
                  .map((item, index) => {
                    const booking = item.booking;
                    return (
                    <motion.div
                      key={booking.id || `booking-${index}`}
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ 
                        delay: 0.3 + index * 0.1, 
                        duration: 0.4,
                        type: "spring",
                        stiffness: 300,
                        damping: 25
                      }}
                    >
                      <TicketCard 
                        booking={booking}
                        onCardClick={() => {
                          if (booking?.id) {
                            onBookingClick(booking.id);
                          }
                        }} 
                      />
                    </motion.div>
                  );
                  })}
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
